import React, { useMemo, useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { projects, categories } from "../mock";
import { useReveal } from "../hooks/useReveal";

function WorkCard({ project, onOpen, index }) {
  const { ref, visible } = useReveal();
  return (
    <button
      ref={ref}
      onClick={() => onOpen(project)}
      style={{ transitionDelay: `${(index % 3) * 90}ms` }}
      className={`work-card group text-left reveal ${visible ? "is-visible" : ""}`}
    >
      <div className="relative overflow-hidden rounded-lg bg-[var(--bg-alt)] aspect-[4/5]">
        <img
          src={project.cover}
          alt={project.title}
          loading="lazy"
          className={`work-img absolute inset-0 w-full h-full ${
            project.fit === "contain" ? "object-contain p-6" : "object-cover"
          } ${
            // White-background marks blend into the warm card instead of sitting in a white box.
            project.blend ? "mix-blend-multiply" : ""
          }`}
        />
        <div className="absolute inset-0 bg-[var(--ink)]/0 group-hover:bg-[var(--ink)]/10 transition-colors duration-500" />
        <div className="absolute top-4 right-4 w-11 h-11 rounded-full bg-[#FAFAF7] flex items-center justify-center translate-y-3 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-400">
          <ArrowUpRight size={18} />
        </div>
      </div>
      <div className="flex items-baseline justify-between mt-4 gap-4">
        <h3 className="font-serif-display text-xl md:text-2xl leading-tight">
          {project.title}
        </h3>
        <span className="text-[13px] text-[var(--muted)] shrink-0">{project.year}</span>
      </div>
      <p className="text-[13px] text-[var(--muted)] mt-1">{project.role}</p>
    </button>
  );
}

export default function Work({ onOpen }) {
  const [filter, setFilter] = useState("All");

  const filtered = useMemo(
    () =>
      filter === "All"
        ? projects
        : projects.filter((p) => p.category === filter),
    [filter]
  );

  return (
    <section id="work" className="py-24 md:py-32 scroll-mt-20">
      <div className="container-wide">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-12">
          <div>
            <span className="section-label">Selected Work</span>
            <h2 className="font-serif-display font-light text-5xl md:text-6xl mt-4 tracking-[0.015em]">
              A few things<br />I&rsquo;m proud of.
            </h2>
          </div>
          {/* Filters */}
          <div className="flex flex-wrap gap-2">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setFilter(c)}
                className={`px-4 py-2 rounded-full text-[13px] border transition-all duration-300 ${
                  filter === c
                    ? "bg-[var(--ink)] text-[#FAFAF7] border-[var(--ink)]"
                    : "border-[var(--line)] text-[var(--muted)] hover:border-[var(--ink)] hover:text-[var(--ink)]"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
          {filtered.map((p, i) => (
            <WorkCard key={p.id} project={p} onOpen={onOpen} index={i} />
          ))}
        </div>

        {filtered.length === 0 && (
          <p className="text-center text-[var(--muted)] py-16">
            No projects in this category yet.
          </p>
        )}
      </div>
    </section>
  );
}
